import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDispatch } from 'react-redux';
import { ToastMessage } from '../../../global/ToastMessage';
import HeaderModal from '../../../global/headerModal';
import { esFechaValida } from '../../../utils/FormatFecha';
import PickerMod from '../../../global/PickersMod';
import useApi from '../../../hooks/useAPI';
import { catGrupoPaqueteProveedor } from '../../../constant/catGrupoPaqueteProveedor';
import { 
  obtenerDescuentosPaqueteMayoristaAction,
  agregarDescuentoPaqueteMayoristaAction 
} from '../../../ducks/proveedorMayoristaDuck';
import { offLoadAction, onLoadAnction } from '../../../ducks/loadDuck';

const FConfigurarDescuentos = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const catalogoGrupoPaquete = useApi(`Supplier/groupPack/${catGrupoPaqueteProveedor.ID_PROVEEDOR_GRAN_TOUR}`);
  const [descuentos, setDescuentos] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
  const [porcentajeDescuento, setPorcentajeDescuento] = useState('');
  const [fechaLimiteDescuento, setFechaLimiteDescuento] = useState(new Date());
  const [loading, setLoading] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(null); // Para controlar carga por botón
  

  // Función para manejar el cambio de fecha
  const handleFechaChange = (date) => {
    if (!esFechaValida({ date })) return;
    setFechaLimiteDescuento(date);
  };

  // Cargar descuentos existentes al abrir el modal
  useEffect(() => {
    if (open) {
      dispatch(obtenerDescuentosPaqueteMayoristaAction({ 
        idProveedor: catGrupoPaqueteProveedor.ID_PROVEEDOR_GRAN_TOUR 
      })).then(response => {
        setDescuentos(response);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, catGrupoPaqueteProveedor.ID_PROVEEDOR_GRAN_TOUR]);

  const handleAgregarDescuento = async () => {
    if (!categoriaSeleccionada || !porcentajeDescuento || !fechaLimiteDescuento) {
      ToastMessage.fire({
        icon: 'warning',
        title: 'Selecciona una categoría e ingresa un porcentaje'
      });
      return;
    }

    // Validar que la fecha no sea anterior al día actual
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaSeleccionada = new Date(fechaLimiteDescuento);
    fechaSeleccionada.setHours(0, 0, 0, 0);

    if (fechaSeleccionada < hoy) {
      ToastMessage.fire({
        icon: 'error',
        title: 'La fecha límite no puede ser anterior al día actual'
      });
      return;
    }

    try {
      dispatch(onLoadAnction({ message: '' }))
      setLoading(true)
      
      // Crear fecha a medianoche
      const fechaMedianoche = new Date(fechaLimiteDescuento);
      fechaMedianoche.setHours(0, 0, 0, 0);

      const response = await dispatch(agregarDescuentoPaqueteMayoristaAction({
        idProveedorGrupoPaquete: parseInt(categoriaSeleccionada),
        porcentaje: parseFloat(porcentajeDescuento),
        fechaLimite: fechaMedianoche.toISOString()
      }));

      if (response === 1) {
        ToastMessage.fire({
          icon: 'success',
          title: 'Descuento agregado correctamente'
        });

        dispatch(obtenerDescuentosPaqueteMayoristaAction({ 
          idProveedor: catGrupoPaqueteProveedor.ID_PROVEEDOR_GRAN_TOUR 
        })).then(response => {
          setDescuentos(response);
        });

        setCategoriaSeleccionada('');
        setPorcentajeDescuento('');
        setFechaLimiteDescuento(new Date());
      } else {
        ToastMessage.fire({
          icon: 'error',
          title: 'Error al agregar descuento'
        });
      }
    } catch (error) {
      ToastMessage.fire({
        icon: 'error',
        title: 'Error al agregar descuento'
      });
    } finally {
      dispatch(offLoadAction())
      setLoading(false)
    }
  };

  // Función para "eliminar" descuento (en realidad lo actualiza con 0% y fecha 01-01-1990)
  const handleEliminarDescuento = async (idProveedorGrupoPaquete) => {
    try {
      setLoadingDelete(idProveedorGrupoPaquete);
      dispatch(onLoadAnction({ message: '' }));
      
      // Crear fecha hardcodeada 01-01-1990
      const fechaEliminacion = new Date('1990-01-01T00:00:00');
      
      const response = await dispatch(agregarDescuentoPaqueteMayoristaAction({
        idProveedorGrupoPaquete: parseInt(idProveedorGrupoPaquete),
        porcentaje: 0,
        fechaLimite: fechaEliminacion.toISOString()
      }));

      if (response === 1) {
        ToastMessage.fire({
          icon: 'success',
          title: 'Descuento eliminado correctamente'
        });

        dispatch(obtenerDescuentosPaqueteMayoristaAction({ 
          idProveedor: catGrupoPaqueteProveedor.ID_PROVEEDOR_GRAN_TOUR 
        })).then(response => {
          setDescuentos(response);
        });
      } else {
        ToastMessage.fire({
          icon: 'error',
          title: 'Error al eliminar descuento'
        });
      }
    } catch (error) {
      ToastMessage.fire({
        icon: 'error',
        title: 'Error al eliminar descuento'
      });
    } finally {
      dispatch(offLoadAction());
      setLoadingDelete(null);
    }
  };

  const formatFechaLegible = (date) => {
    if (!date) return 'Sin fecha';
    return new Date(date).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const esFechaEliminada = (date) => {
    if (!date) return false;
    const fecha = new Date(date);
    return fecha.getFullYear() === 1990 && fecha.getMonth() === 0 && fecha.getDate() === 1;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <HeaderModal titulo={`Configurar Descuentos`} handleClose={onClose} />
      
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          <strong>Descuentos por Categoría</strong>
        </Typography>
        
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: '16px' }}>
          <Grid item xs={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={categoriaSeleccionada}
                onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                label="Categoría"
              >
                <MenuItem value="" disabled>
                  Seleccione una categoría
                </MenuItem>
                {catalogoGrupoPaquete?.map((categoria) => (
                  <MenuItem 
                    key={categoria.IdProveedorGrupoPaquete} 
                    value={categoria.IdProveedorGrupoPaquete}
                    disabled={descuentos.some(d => 
                      d.categoriaId === categoria.grupoPaquete.IdGrupoPaquete
                    )}
                  >
                    {categoria.grupoPaquete.Descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              label="Porcentaje de descuento"
              variant="outlined"
              value={porcentajeDescuento}
              onChange={(e) => {
                // Validar que solo tenga máximo 2 decimales
                const value = e.target.value;
                if (value === '') {
                  setPorcentajeDescuento('');
                  return;
                }
                
                // Expresión regular para validar números con máximo 2 decimales
                const regex = /^\d*\.?\d{0,2}$/;
                if (regex.test(value)) {
                  setPorcentajeDescuento(value);
                }
              }}
              InputProps={{
                endAdornment: '%',
                inputProps: { 
                  min: 0, 
                  max: 50,
                  step: 0.1
                }
              }}
              onBlur={(e) => {
                if (e.target.value) {
                  const roundedValue = parseFloat(e.target.value).toFixed(2);
                  setPorcentajeDescuento(roundedValue);
                }
              }}
            />
          </Grid>
          
          <Grid item xs={3}>
            <PickerMod
              id="fecha-limite-descuento"
              label="Fecha límite"
              selectedDate={fechaLimiteDescuento}
              setSelectedDate={handleFechaChange}
              disablePast
              disableFuture={false}
            />
          </Grid>

          <Grid item xs={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={handleAgregarDescuento}
              fullWidth
              disabled={
                !categoriaSeleccionada || 
                !porcentajeDescuento || 
                !fechaLimiteDescuento ||
                loading
              }
              style={{ height: '56px' }}
            >
              {loading ? 'Agregando...' : 'Agregar'}
            </Button>
          </Grid>
        </Grid>

        {/* Listado de descuentos por categoría */}
        {descuentos.filter(d => !esFechaEliminada(d.FechaFin)).length > 0 && (
          <Paper variant="outlined" style={{ marginBottom: '24px', maxHeight: '20rem', overflowY: 'auto' }} >
            <Table>
              <TableBody>
                {descuentos
                  .filter(d => !esFechaEliminada(d.FechaFin)) // Filtrar los "eliminados"
                  .map((descuento) => {
                    const hoy = new Date();
                    hoy.setHours(0, 0, 0, 0);
                    
                    const fechaDescuento = new Date(descuento.FechaFin);
                    fechaDescuento.setHours(0, 0, 0, 0);
                    
                    const estaCaducado = fechaDescuento < hoy;
                    
                    return (
                      <TableRow key={descuento.IdProveedorGrupoPaquete}>
                        <TableCell>
                          <Typography>
                            <strong>{descuento?.ProveedorGrupoPaquete?.grupoPaquete?.Descripcion}</strong>
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography>{descuento.Porcentaje}%</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography style={{ color: estaCaducado ? 'red' : 'inherit' }}>
                            {formatFechaLegible(descuento.FechaFin)}
                            {estaCaducado && ' (Caducado)'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleEliminarDescuento(descuento?.ProveedorGrupoPaquete?.IdProveedorGrupoPaquete)}
                            disabled={loadingDelete === descuento?.ProveedorGrupoPaquete?.IdProveedorGrupoPaquete}
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FConfigurarDescuentos;